import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../ThemeToggle';
import PasswordStrength, { scorePassword } from './PasswordStrength';

// Import the SVG logo
import logo from '../../assets/logo.svg';

const LoginPage = () => {
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we should start in register mode
  React.useEffect(() => {
    if (location.state?.mode === 'register') {
      setIsRegisterMode(true);
    }
  }, [location.state]);

  // EmailVerification navigates here with a success message. It used to be
  // dropped on the floor, so a user who had just verified their email landed on
  // a bare login form with no sign anything had happened.
  const notice: string | undefined = location.state?.message;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isRegisterMode) {
      // Registration validation
      if (!username || !email || !password) {
        setError('Please fill in all required fields');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      const strength = scorePassword(password, email || username, username);
      if (!strength.acceptable) {
        setError(strength.hint || 'Please choose a stronger password (at least 8 characters)');
        return;
      }

      try {
        await register(username, email, password, fullName || undefined, company || undefined);
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Registration failed');
      }
    } else {
      // Login
      if (!username || !password) {
        setError('Please enter email and password');
        return;
      }

      try {
        // login() picks the landing route (Dashboard, or the Guide for a new
        // account's first logins). A page the user was bounced off still wins.
        const landing = await login(username, password);
        const from = location.state?.from?.pathname || landing;
        navigate(from, { replace: true });
      } catch (err) {
        setError('Invalid credentials');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-white via-coral-50 to-white dark:from-background dark:via-background dark:to-background">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md bg-black/30 backdrop-blur-sm rounded-lg p-6 border border-coral-500/20">
        <div className="text-center mb-6">
          {/* Fixed: Use imported SVG */}
          <img 
            src={logo} 
            alt="ViewPoint Logo" 
            className="w-12 h-12 mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-white">
            {isRegisterMode ? 'Create Account' : 'Welcome to ViewPoint'}
          </h2>
          <p className="text-foreground-muted text-sm mt-2">
            {isRegisterMode ? 'Start your Discovery plan trial' : 'Sign in to your account'}
          </p>
          {isRegisterMode && (
            <p className="text-foreground-muted text-xs mt-1">
              <a
                href="/pricing"
                className="font-bold underline hover:text-foreground"
              >
                View pricing plans
              </a> • Free 14-day trial included
            </p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {notice && !error && (
            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-green-700 dark:text-green-300">
              <CheckCircle className="w-4 h-4 shrink-0" />
              <p className="text-sm">{notice}</p>
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-200">
              <AlertCircle className="w-4 h-4" />
              <p className="text-sm">{error}</p>
            </div>
          )}
          
          <div className="space-y-2">
            <input
              type="email"
              placeholder="Email Address"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full p-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
              required
            />
          </div>

          {isRegisterMode && (
            <div className="space-y-2">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 pr-10 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-coral-400 hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {isRegisterMode && (
              <PasswordStrength password={password} email={email || username} username={username} />
            )}
          </div>

          {isRegisterMode && (
            <>
              <div className="space-y-2">
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-2 pr-10 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-coral-400 hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Full Name (optional)"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full p-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
                />
              </div>

              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Company (optional)"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full p-2 bg-coral-500/10 border border-coral-500/20 rounded-lg text-foreground placeholder-coral-400/60 focus:outline-none focus:ring-2 focus:ring-coral-500/40"
                />
              </div>
            </>
          )}

          <button
            type="submit"
            className="w-full py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors"
          >
            {isRegisterMode ? 'Create Account' : 'Sign In'}
          </button>

          <div className="text-center space-y-2">
            {!isRegisterMode && (
              <div>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-foreground-muted hover:text-foreground-secondary text-sm underline"
                >
                  Forgot your password?
                </button>
              </div>
            )}
            <div>
              <button
                type="button"
                onClick={() => {
                  if (isRegisterMode) {
                    setIsRegisterMode(false);
                    setError('');
                  } else {
                    navigate('/register');
                  }
                }}
                className="text-foreground-muted hover:text-foreground-secondary text-sm underline"
              >
                {isRegisterMode ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
              </button>
            </div>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                className="text-coral-400 hover:text-foreground text-sm font-medium underline"
              >
                Explore Plans & Pricing
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;